import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  GoogleMap,
  MarkerF,
  Marker,
  InfoWindow,
  CircleF,
} from "@react-google-maps/api";
import "./UniversityMaps.scss";
import UniversityMapicon from "../../Assets/University/univmap.svg";
import shareIcon from "../../Assets/University/universityShareIcon.svg";
import LikeIcon from "../../Assets/University/UniversityLikeIcon.svg";
import locationIcon from "../../Assets/Tutor/location.svg";
import {
  GetSessionStorageData,
  SetSessionStorageData,
  encryptPassData,
  nullUndefinedEmptyCheck,
} from "../../Utilities/Helpers/Helper";
import {
  API_END_POINTS,
  AUTHENTICATION,
  GLOBAL_CONSTANTS,
  ROUTES,
  TUTORMAPCONSTANT,
} from "../../Constants/Constants";
import {
  setLocation,
  setLocationName,
  setLoginSignUpOpen,
} from "../../Redux/Reducer/Reducer";
import { useDispatch, useSelector } from "react-redux";
import UniversityFill from "../../Assets/HeartFill/UniversityFill.svg";
import toast from "react-hot-toast";
import { axiosInstance } from "../../Utilities/Interceptor/Interceptor";
import WebShareComponent from "../WebShare/WebShare";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";

interface Location {
  lat: number;
  lng: number;
}

interface MapProps {
  zoom: number;
  radius: number;
  lat: any;
  lang: any;
  containerStyle: any;
  disable?: boolean;
  zoomControl?: boolean;
  clickableIcons?: boolean;
  draggable?: boolean;
  getOneTutor?: (data: any) => void;
  UniversityList?: any[];
  initSort?: any;
  searchValue?: string;
  initFilter?: any;
  filter?: any;
  language?: string;
  isLoaded?: boolean;
  getLatAndLong?: (lat: any, lng: any) => void;
  onLocationChange?: (lat: any, lng: any) => void;
}

const UniversityMaps = ({
  zoom,
  radius,
  containerStyle,
  disable,
  zoomControl,
  draggable,
  clickableIcons,
  searchValue,
  getOneTutor,
  lat,
  lang,
  filter,
  initFilter,
  UniversityList,
  initSort,
  isLoaded: propIsLoaded,
  onLocationChange,
  getLatAndLong,
}: MapProps & { isLoaded: boolean }) => {
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [universityList, setUniversityList] = useState<any>(UniversityList || []);
  const [userProfileImg, setUserProfileImg] = useState<string | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showMapTooltip, setShowMapTooltip] = useState<boolean>(false);
  const [showMapTooltipMobile, setShowMapTooltipMobile] = useState<boolean>(false);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isMobileView, setIsMobileView] = useState<boolean>(window.innerWidth <= 636);

  const language: any = useSelector((state: any) => state.languageReducer.language);
  const HeaderLocation: any = useSelector((state: any) => state.locationReducer.location);
  const reduxMapLoaded: boolean = useSelector((state: any) => state?.googleMapLoadedReducer?.isGoogleMapLoaded);
  const isLoaded = typeof propIsLoaded === "boolean" ? propIsLoaded : reduxMapLoaded;
  const limit = 10;
  const [page, setPage] = useState<number>(1);

  const mapRef = useRef<google.maps.Map | null>(null);
  const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const locationKey = `${currentLocation?.lat || ''},${currentLocation?.lng || ''}`;

 const debouncedUniversityListApi = useMemo(() =>
  debounce((pageParam: number) => {
    const pay = payload(pageParam);
    const headerLang =
      language === GLOBAL_CONSTANTS.LANGUAGES.ENGLISH ? "en" : "ar";

    axiosInstance
      .post(`${API_END_POINTS.UNIVERSITYLIST}`, pay, {
        headers: { "Accept-Language": headerLang },
      })
      .then((response: any) => {
        setUniversityList(response?.data?.result?.data || []);
      })
      .catch((error) => {
        console.error("Error fetching university list:", error);
      });
  }, 500),
  [locationKey, filter, searchValue, radius, language]
);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= 636);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setShowMapTooltipMobile(isMobileView);
  }, [isMobileView]);

  useEffect(() => {
    if (!isLoaded) return;

    let finalLocation: Location | null = null;

    if (HeaderLocation?.lat && HeaderLocation?.lng) {
      finalLocation = { lat: HeaderLocation.lat, lng: HeaderLocation.lng };
    }
    else {
      const saved = GetSessionStorageData(AUTHENTICATION.SHARED.UNIVERSITY_LOCATION);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed?.lat && parsed?.lng) {
            finalLocation = { lat: parsed.lat, lng: parsed.lng };
          }
        } catch (e) {
          console.warn("Invalid UNIVERSITY_LOCATION in session", e);
        }
      }
    }
    if (!finalLocation && lat && lang) {
      finalLocation = { lat: parseFloat(lat), lng: parseFloat(lang) };
    }
    if (!finalLocation) {
      finalLocation = { lat: 25.2048, lng: 55.2708 }; 
    }

    const locationChanged =
      !currentLocation ||
      currentLocation.lat !== finalLocation.lat ||
      currentLocation.lng !== finalLocation.lng;

    if (locationChanged) {
      setCurrentLocation(finalLocation);

      const reduxOutdated =
        !HeaderLocation ||
        HeaderLocation.lat !== finalLocation.lat ||
        HeaderLocation.lng !== finalLocation.lng;

      if (reduxOutdated) {
        dispatch(setLocation(finalLocation));
      }

      SetSessionStorageData(AUTHENTICATION.SHARED.UNIVERSITY_LOCATION, JSON.stringify(finalLocation));
    }

    const profile = GetSessionStorageData(AUTHENTICATION.SHARED.PROFILE_PICTURE);
    if (nullUndefinedEmptyCheck(profile) && userProfileImg !== profile) {
      setUserProfileImg(profile);
    }
  }, [isLoaded, HeaderLocation, lat, lang, dispatch, currentLocation, userProfileImg]);

  useEffect(() => {
    if (isLoaded && currentLocation?.lat && currentLocation?.lng) {
      debouncedUniversityListApi(page);
    }
    return () => {
      debouncedUniversityListApi.cancel();
    };
  }, [isLoaded, locationKey, searchValue, radius, filter, debouncedUniversityListApi]);

const payload = (pageParam: number) => {
    const priceValue =
      initFilter.price[0] === filter.price[0] && initFilter.price[1] === filter.price[1]
        ? []
        : { min: filter.price[0], max: filter.price[1] };
    const qsValue =
      initFilter.qs_ranking[0] === filter.qs_ranking[0] && initFilter.qs_ranking[1] === filter.qs_ranking[1]
        ? []
        : { min: filter.qs_ranking[0], max: filter.qs_ranking[1] };
    const shValue =
      initFilter.sh_ranking[0] === filter.sh_ranking[0] && initFilter.sh_ranking[1] === filter.sh_ranking[1]
        ? []
        : { min: filter.sh_ranking[0], max: filter.sh_ranking[1] };
    const theValue =
      initFilter.the_ranking[0] === filter.the_ranking[0] && initFilter.the_ranking[1] === filter.the_ranking[1]
        ? []
        : { min: filter.the_ranking[0], max: filter.the_ranking[1] };

    return {
      latitude: currentLocation?.lat?.toString(),
      longitude: currentLocation?.lng?.toString(),
      radius: filter.radius,
      price: priceValue,
      qs_ranking: qsValue,
      sh_ranking: shValue,
      the_ranking: theValue,
      programs: filter.programs,
      language_instruction: filter.language_instruction,
      page: pageParam,
      limit: limit
    };
  };

  const navigateDetailedView = (Details: any) => {
    navigate(ROUTES.UNIVERSITY_DETAILE_VIEW + "?id=" + encryptPassData(Details?.id), {
      state: { tutor_Detailes: Details },
    });
  };

  const mapOptions = {
    disableDefaultUI: disable,
    zoomControl: zoomControl,
    draggable: draggable,
    clickableIcons: clickableIcons,
    disableDoubleClickZoom: disable,
    gestureHandling: "greedy",
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
      },
    ],
  };

  useEffect(() => {
    if (initSort?.price || initSort?.rating) {
      handleProceedSort(initSort?.price, initSort?.rating);
    }
  }, [initSort, universityList]);

  const handleProceedSort = (checkedPrice: any, checkedRating: any) => {
    const sortedData = [...universityList];
    if (checkedPrice === "high") {
      sortedData.sort((a: any, b: any) => b?.least_price - a?.least_price);
    } else if (checkedPrice === "low") {
      sortedData.sort((a: any, b: any) => a?.least_price - b?.least_price);
    } else if (checkedRating === "high") {
      sortedData.sort((a: any, b: any) => b?.least_rating - a?.least_rating);
    } else if (checkedRating === "low") {
      sortedData.sort((a: any, b: any) => a?.least_rating - b?.least_rating);
    }
    setUniversityList([...sortedData]);
  };

  const handleChangeFavourites = (val: any, id: any) => {
    const accessToken = GetSessionStorageData(AUTHENTICATION.SHARED.ACCESS_TOKEN);
    if (nullUndefinedEmptyCheck(accessToken)) {
      const payload = {
        service_provider_type: GLOBAL_CONSTANTS.INITIAL_VALUES.FIVE_INT,
        service_provider: id,
        is_favourite: !val,
      };
      axiosInstance
        .post(`${API_END_POINTS.FAVOURITE__ADD}`, payload)
        .then((Response: any) => {
          if (Response?.data?.status) {
            toast.success(Response?.data?.message, {
              position: GLOBAL_CONSTANTS.INITIAL_VALUES.TOAST_POSITION,
            });
            debouncedUniversityListApi(page);
          }
        })
        .catch((error) => {
          console.error("Error updating favorites:", error);
        });
    } else {
      dispatch(setLoginSignUpOpen(GLOBAL_CONSTANTS.INITIAL_VALUES.TRUE));
    }
  };

  const reverseGeocode = useCallback(
    (latitude: number, longitude: number) => {
      if (!window.google?.maps) return;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: latitude, lng: longitude } },
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status === "OK" && results?.[0]) {
            const comp = results[0].address_components;
            const city = comp.find((c) =>
              c.types.includes("locality") || c.types.includes("administrative_area_level_1")
            );
            const country = comp.find((c) => c.types.includes("country"));
            const display = city && country ? `${city.long_name}, ${country.long_name}` : country?.long_name ?? "";

            dispatch(setLocationName(display));
            if (city) SetSessionStorageData(AUTHENTICATION.SHARED.REGION_NAME, city.long_name);
            if (country) SetSessionStorageData(AUTHENTICATION.SHARED.COUNTRY_NAME, country.long_name);
          }
        }
      );
    },
    [dispatch]
  );

  const handleMapDragEnd = useCallback(() => {
    if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    dragTimeoutRef.current = setTimeout(() => {
      if (!mapRef.current) return;
      const c = mapRef.current.getCenter();
      if (!c) return;
      const newLat = c.lat();
      const newLng = c.lng();
      const location = { lat: newLat, lng: newLng };
      setCurrentLocation(location);
      dispatch(setLocation(location));
      SetSessionStorageData(AUTHENTICATION.SHARED.UNIVERSITY_LOCATION, JSON.stringify(location));
      reverseGeocode(newLat, newLng);
      setPage(1);
      debouncedUniversityListApi(page);
      onLocationChange?.(newLat, newLng);
      getLatAndLong?.(newLat, newLng);
    }, 400);
  }, [dispatch, reverseGeocode, debouncedUniversityListApi, onLocationChange, getLatAndLong]);

  const handleMarkerDragEnd = (event: google.maps.MapMouseEvent) => {
    const newLat = event?.latLng?.lat();
    const newLng = event?.latLng?.lng();
    if (newLat === undefined || newLng === undefined) return;
    const locationData = { lat: newLat, lng: newLng };
    setCurrentLocation(locationData);
    dispatch(setLocation(locationData));
    SetSessionStorageData(AUTHENTICATION.SHARED.UNIVERSITY_LOCATION, JSON.stringify(locationData));
    reverseGeocode(newLat, newLng);
    setPage(1);
    debouncedUniversityListApi(page);
    onLocationChange?.(newLat, newLng);
    getLatAndLong?.(newLat, newLng);
  };

  const handleMapClick = (event: any) => {
    const newLat = event?.latLng?.lat();
    const newLng = event?.latLng?.lng();
    if (newLat === undefined || newLng === undefined) return;
    const locationData = { lat: newLat, lng: newLng };
    setSelectedLocation(null);
    setCurrentLocation(locationData);
    dispatch(setLocation(locationData));
    SetSessionStorageData(AUTHENTICATION.SHARED.UNIVERSITY_LOCATION, JSON.stringify(locationData));
    reverseGeocode(newLat, newLng);
    setPage(1);
    debouncedUniversityListApi(page);
    onLocationChange?.(newLat, newLng);
    getLatAndLong?.(newLat, newLng);
    if (isMobileView) setShowMapTooltipMobile(false);
  };

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    };
  }, []);

  const profileIcon = useMemo(() => {
    if (
      isLoaded &&
      userProfileImg &&
      typeof userProfileImg === "string" &&
      userProfileImg.trim() !== "" &&
      window.google?.maps
    ) {
      return {
        url: userProfileImg,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 20),
      };
    }
    return undefined;
  }, [isLoaded, userProfileImg]);

  return isLoaded ? (
    <div style={{ position: "relative", ...containerStyle }}>
      <div
        onMouseEnter={() => !isMobileView && setShowMapTooltip(true)}
        onMouseMove={(e: React.MouseEvent<HTMLDivElement>) => {
          if (!isMobileView) {
            const rect = e.currentTarget.getBoundingClientRect();
            setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }
        }}
        onMouseLeave={() => setShowMapTooltip(false)}
        onTouchStart={() => setShowMapTooltipMobile(false)}
      >
        <GoogleMap
          id={TUTORMAPCONSTANT.MAP__ID}
          mapContainerStyle={containerStyle}
          zoom={zoom}
          center={currentLocation || HeaderLocation}
          options={mapOptions}
          onClick={handleMapClick}
          onLoad={onLoad}
          onDragEnd={handleMapDragEnd}
        >
          <CircleF
            center={currentLocation || HeaderLocation}
            radius={radius}
            options={{
              fillColor: "#178FFF",
              strokeColor: "rgba(79, 139, 255, 0.60)",
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillOpacity: 0.1,
            }}
          />

          {(currentLocation || HeaderLocation) && (
            <MarkerF
              position={currentLocation || HeaderLocation}
              draggable={true}
              zIndex={1000}
              shape={{ coords: [17, 17, 18], type: "circle" }}
              icon={profileIcon}
              onDrag={(e: google.maps.MapMouseEvent) => {
                const liveLat = e?.latLng?.lat();
                const liveLng = e?.latLng?.lng();
                if (liveLat !== undefined && liveLng !== undefined) {
                  setCurrentLocation({ lat: liveLat, lng: liveLng });
                  onLocationChange?.(liveLat, liveLng);
                  getLatAndLong?.(liveLat, liveLng);
                }
              }}
              onDragEnd={handleMarkerDragEnd}
              onClick={() => setShowMapTooltip(false)}
            />
          )}

          {universityList?.map((data: any) => {
            const position = {
              lat: parseFloat(data.latitude || 0),
              lng: parseFloat(data.longitude || 0),
            };
            const isActive = String(lat) === String(data.latitude) && String(lang) === String(data.longitude);

            return (
              <Marker
                key={`${data.id}-university`}
                position={position}
                icon={{
                  url: UniversityMapicon,
                  scaledSize: new google.maps.Size(isActive ? 65 : 40, isActive ? 65 : 40),
                  anchor: new google.maps.Point(isActive ? 32.5 : 20, isActive ? 32.5 : 20),
                }}
                zIndex={500}
                onMouseOver={() => setSelectedLocation(data)}
                onClick={() => setSelectedLocation(data)}
                animation={isActive ? google.maps.Animation.DROP : undefined}
              >
                {selectedLocation === data && (
                  <InfoWindow
                    onCloseClick={() => setSelectedLocation(null)}
                    options={{ maxWidth: 372 }}
                  >
                    <div
                      onClick={() => navigateDetailedView(data)}
                      onMouseEnter={() => setSelectedLocation(data)}
                      onMouseLeave={() => setSelectedLocation(null)}
                    >
                      <div
                        className="Hover__Card"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (clickableIcons && isMobileView) {
                            navigateDetailedView(data);
                          } else if (clickableIcons && getOneTutor) {
                            getOneTutor(data);
                          }
                        }}
                      >
                        <div className="rightside_contant">
                          <div className="w-100">
                            <div className="user__Detailes">
                              <div className="notranslate user__name" translate="no">
                                {language === "English"
                                  ? data?.university_name
                                  : data?.arabic_university_name || data?.university_name}
                              </div>
                              <div className="user__icon__wrapper" onClick={(e) => e.stopPropagation()}>
                                <WebShareComponent
                                  params={ROUTES.UNIVERSITY_DETAILE_VIEW + "?id=" + encryptPassData(data?.id)}
                                >
                                  <img src={shareIcon} alt="share" />
                                </WebShareComponent>
                                <img
                                  src={data?.is_favourite ? UniversityFill : LikeIcon}
                                  alt="favorite"
                                  onClick={() => handleChangeFavourites(data?.is_favourite, data?.id)}
                                />
                              </div>
                            </div>
                            <div className="user__Detailes__location">
                              <img src={locationIcon} alt="location" className="grayIcons" />
                              <div className="graytext info_location" title={data?.city}>
                                {data?.city}
                              </div>
                            </div>
                            <div className="University__Detailes__rating">
                              <div className="Detailes__rating__contant">
                                <span>{t("QS")} :</span>
                                <div>
                                  {data?.QS_ranking ? data?.QS_ranking : GLOBAL_CONSTANTS.INITIAL_VALUES.NA}
                                </div>
                              </div>
                              <div className="Detailes__rating__contant">
                                <span>{t("THE")} :</span>
                                <div>
                                  {data?.THE_ranking ? data?.THE_ranking : GLOBAL_CONSTANTS.INITIAL_VALUES.NA}
                                </div>
                              </div>
                              <div className="Detailes__rating__contant">
                                <span>{t("SH")} :</span>
                                <div>
                                  {data?.SH_ranking ? data?.SH_ranking : GLOBAL_CONSTANTS.INITIAL_VALUES.NA}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            );
          })}
        </GoogleMap>
      </div>

      {/* Desktop Tooltip */}
      {showMapTooltip && HeaderLocation && (
        <div
          style={{
            position: "absolute",
            left: tooltipPos.x,
            top: tooltipPos.y,
            zIndex: 1200,
            background: "#fff",
            padding: "6px 10px",
            borderRadius: 6,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            pointerEvents: "none",
            fontSize: 13,
            color: "#111",
          }}
        >
          {language === "English"
            ? "Drag the pin to select your preferred location"
            : "اسحب الدبوس لتحديد موقعك المفضل"}
        </div>
      )}

      {/* Mobile Tooltip */}
      {isMobileView && showMapTooltipMobile && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: 12,
            zIndex: 1200,
            background: "rgba(255,255,255,0.95)",
            padding: "8px 12px",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            pointerEvents: "none",
            fontSize: 13,
            color: "#111",
            maxWidth: "90%",
            textAlign: "center",
          }}
        >
          {language === "English"
            ? "Tap map to select preferred region or drag the marker"
            : "اضغط على الخريطة لاختيار المنطقة المفضلة أو اسحب العلامة"}
        </div>
      )}
    </div>
  ) : (
    <div>Loading map...</div>
  );
};

export default React.memo(UniversityMaps);